import { useMemo } from 'react';
import {
	useQuery,
	useQueries,
	type UseQueryOptions,
} from '@tanstack/react-query';
import {
	type LWApiPublicNetworkIpListPublicParamsRaw,
	type LWApiPublicNetworkIpListPublicResultRaw,
} from '@liquidweb-dev/club-sauce/public/network/ip/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';

export const NETWORK_IP_LIST_PUBLIC_QUERY_KEY = '/network/ip/listPublic';

const useNetworkIpListPublic = (
	params: LWApiPublicNetworkIpListPublicParamsRaw,
	options?: UseQueryOptions<LWApiPublicNetworkIpListPublicResultRaw, ApiErrorI>,
) =>
	useQuery({
		queryKey: [NETWORK_IP_LIST_PUBLIC_QUERY_KEY, params],
		queryFn: () => api.network.ip.apiRaw.listPublic(params),
		...options,
	});

export const usePollNetworkIpListPublic = (
	params: LWApiPublicNetworkIpListPublicParamsRaw,
	options?: UseQueryOptions<LWApiPublicNetworkIpListPublicResultRaw, ApiErrorI>,
) =>
	useNetworkIpListPublic(params, {
		refetchInterval: 30000,
		...options,
	});

interface NetworkIpListPublicForServersParams
	extends Omit<LWApiPublicNetworkIpListPublicParamsRaw, 'uniq_id'> {
	uniq_ids: string[];
	expand_ips: 1;
	page_size: 9999;
}

export const useNetworkIpListPublicForServers = (
	params: NetworkIpListPublicForServersParams,
	options?: UseQueryOptions<LWApiPublicNetworkIpListPublicResultRaw, ApiErrorI>,
) => {
	const { uniq_ids: uniqIds, ...restParams } = params;
	const publicIPsQueries = useQueries({
		queries: uniqIds.map((uniqId) => ({
			queryKey: [NETWORK_IP_LIST_PUBLIC_QUERY_KEY, { uniq_id: uniqId }],
			queryFn: () =>
				api.network.ip.apiRaw.listPublic({ uniq_id: uniqId, ...restParams }),
			...options,
		})),
	});

	const publicIPs = useMemo(() => {
		return publicIPsQueries
			.map((query) => query.data?.items)
			.filter(Boolean)
			.flat();
	}, [publicIPsQueries]);

	const isLoading = publicIPsQueries.some((query) => query.isLoading);
	const isError = publicIPsQueries.some((query) => query.isError);
	const errors = publicIPsQueries
		.map((query) => query.error)
		.filter(Boolean) as ApiErrorI[];

	return {
		data: publicIPs,
		isLoading,
		isError,
		errors,
		queries: publicIPsQueries,
	};
};

export default useNetworkIpListPublic;
